import type { TFunction } from 'i18next';
import type { ProjectStatusValue } from '@kreart/shared';

/**
 * Project-status labels.
 *
 * `REOFFER` is a single status whose display grows with how many times a
 * project has entered it (`Project.reofferCount`): generation 1 shows plain
 * "Reoffer", generation 2+ shows "Reoffer (2nd)", "(3rd)", … The ordinal is
 * English-only; other locales interpolate the raw number (`{{n}}`).
 */

/** English ordinal suffix: 1→"1st", 2→"2nd", 3→"3rd", 11→"11th", 21→"21st". */
export function englishOrdinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/**
 * Label for a re-offer at the given generation, using the requested i18n
 * namespace: `projects.status.*` for badges, `projects.transition.actions.*`
 * for the action button (which carries the verb form in some locales).
 */
function reofferLabel(
  t: TFunction,
  ns: 'projects.status' | 'projects.transition.actions',
  generation: number,
): string {
  if (generation <= 1) return t(`${ns}.REOFFER`);
  // `ord` (not `ordinal`) — i18next reserves `ordinal` for ordinal plurals.
  return t(`${ns}.REOFFER_nth`, { n: generation, ord: englishOrdinal(generation) });
}

/** Badge/status label. For REOFFER, `reofferCount` picks the generation. */
export function projectStatusLabel(
  t: TFunction,
  status: ProjectStatusValue,
  reofferCount = 0,
): string {
  if (status === 'REOFFER') return reofferLabel(t, 'projects.status', Math.max(reofferCount, 1));
  return t(`projects.status.${status}`);
}

/**
 * Label for the "Reoffer" transition button. It targets the *next*
 * generation, so from a project with `reofferCount` N it reads the N+1 label
 * (count 0 → "Reoffer", count 1 → "Reoffer (2nd)", …).
 */
export function reofferActionLabel(t: TFunction, reofferCount = 0): string {
  return reofferLabel(t, 'projects.transition.actions', reofferCount + 1);
}
